import { Property } from "../types/property";
import { API_BASE_URL } from "./config";

const PROPERTIES_ENDPOINT = `${API_BASE_URL}/api/v1/properties`;
const RETRYABLE_STATUS_CODES = new Set([500, 502, 503, 504]);
const MAX_FETCH_ATTEMPTS = 3;

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

async function fetchWithTransientRetry(endpoint: string): Promise<Response> {
  let lastNetworkError: unknown;

  for (let attempt = 1; attempt <= MAX_FETCH_ATTEMPTS; attempt++) {
    try {
      const response = await fetch(endpoint);
      if (
        response.ok ||
        !RETRYABLE_STATUS_CODES.has(response.status) ||
        attempt === MAX_FETCH_ATTEMPTS
      ) {
        return response;
      }
    } catch (error) {
      lastNetworkError = error;
      if (attempt === MAX_FETCH_ATTEMPTS) throw error;
    }

    await wait(400 * attempt);
  }

  throw lastNetworkError ?? new Error("Property request failed");
}

export function propertyBrochureUrl(propertyId: number): string {
  return `${PROPERTIES_ENDPOINT}/${propertyId}/brochure`;
}

export interface FetchPropertiesParams {
  near?: string;
  keyword?: string;
  minPrice?: number;
  maxPrice?: number;
  bedrooms?: number;
  bathrooms?: number;
}

export async function fetchProperties(
  params?: FetchPropertiesParams
): Promise<Property[]> {
  const searchParams = new URLSearchParams();
  if (params?.near) searchParams.set("near", params.near);
  if (params?.keyword) searchParams.set("keyword", params.keyword);
  if (params?.minPrice != null)
    searchParams.set("minPrice", String(params.minPrice));
  if (params?.maxPrice != null)
    searchParams.set("maxPrice", String(params.maxPrice));
  if (params?.bedrooms != null)
    searchParams.set("bedrooms", String(params.bedrooms));
  if (params?.bathrooms != null)
    searchParams.set("bathrooms", String(params.bathrooms));

  const qs = searchParams.toString();
  const endpoint = qs ? `${PROPERTIES_ENDPOINT}?${qs}` : PROPERTIES_ENDPOINT;

  let response: Response;
  try {
    response = await fetchWithTransientRetry(endpoint);
  } catch {
    throw new Error(
      `Failed to fetch properties from ${endpoint}. Is letting-engine-api running on port 5050?`
    );
  }

  if (!response.ok) {
    throw new Error(
      `Failed to fetch properties from ${endpoint} (${response.status} ${response.statusText})`
    );
  }

  const data = await response.json();

  if (!Array.isArray(data)) {
    throw new Error("Invalid properties payload");
  }

  return data as Property[];
}
